// Same-page anchor links - e.g. a hero button whose URL is "#book-a-review",
// pointing at a block given that HTML anchor (Block > Advanced; see
// arise_apply_block_anchor() in php/block.php). For every such link:
//
// - scrolls to the target with an offset, so the fixed header doesn't
//   cover its heading - smoothly, or jumping straight there if the
//   visitor has asked for reduced motion;
// - then moves keyboard focus to the target's heading (or, with none, its
//   first form field, or the section itself), so keyboard and
//   screen-reader users land in the right place too;
// - updates the URL to the #anchor, and handles arriving at one (a shared
//   link, or a link from another page) and back/forward the same way.
//
// The offset is also published as --anchor-offset on <html>, which
// base/_base.scss uses for scroll-margin-top - so even the browser's own
// jump to a #anchor on page load stops below the header.
(function () {
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // The fixed header's bottom edge, plus a little breathing room.
    function headerOffset() {
        var header = document.querySelector('.js-heading-links');
        var bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

        return Math.round(bottom) + 24;
    }

    function updateOffsetProperty() {
        document.documentElement.style.setProperty('--anchor-offset', headerOffset() + 'px');
    }

    function targetFromHash(hash) {
        if (!hash || hash.length < 2) {
            return null;
        }

        var id;

        try {
            id = decodeURIComponent(hash.slice(1));
        } catch (e) {
            return null;
        }

        return document.getElementById(id);
    }

    // An empty .block-anchor marker (a block whose outer element already
    // had an id) stands in for the block right after it.
    function sectionFor(target) {
        if (target.classList.contains('block-anchor') && target.nextElementSibling) {
            return target.nextElementSibling;
        }

        return target;
    }

    function focusTargetIn(section) {
        var heading = section.matches('h1, h2, h3, h4, h5, h6')
            ? section
            : section.querySelector('h1, h2, h3, h4, h5, h6');

        if (heading) {
            return heading;
        }

        var fields = section.querySelectorAll('input:not([type="hidden"]), select, textarea');

        for (var i = 0; i < fields.length; i++) {
            if (!fields[i].disabled && fields[i].offsetParent !== null) {
                return fields[i];
            }
        }

        return section;
    }

    function focusWithoutScrolling(el) {
        // Headings and sections aren't focusable by default - make this one
        // focusable from script only (not added to the tab order).
        if (!el.matches('a[href], button, input, select, textarea, [tabindex]')) {
            el.setAttribute('tabindex', '-1');
        }

        el.focus({ preventScroll: true });
    }

    // Resolves once scrolling has settled - on 'scrollend' where supported,
    // otherwise once the position stops changing (with a hard cap, so focus
    // always moves even if something keeps nudging the page).
    function afterScroll(callback) {
        var done = false;
        var lastY = window.scrollY;
        var stableFrames = 0;
        var startedAt = Date.now();

        function finish() {
            if (done) {
                return;
            }
            done = true;
            window.removeEventListener('scrollend', finish);
            callback();
        }

        window.addEventListener('scrollend', finish, { once: true });

        (function poll() {
            if (done) {
                return;
            }

            if (window.scrollY === lastY) {
                stableFrames++;
            } else {
                stableFrames = 0;
                lastY = window.scrollY;
            }

            if (stableFrames > 6 || Date.now() - startedAt > 3000) {
                finish();
                return;
            }

            window.requestAnimationFrame(poll);
        })();
    }

    // How far the section's top is from where it should sit (just below
    // the header), in px.
    function distanceFromRest(section) {
        return section.getBoundingClientRect().top - headerOffset();
    }

    function goTo(target, options) {
        var section = sectionFor(target);
        var smooth = options.smooth && !reducedMotion.matches;
        var passes = 0;

        updateOffsetProperty();

        // Scrolls, then re-checks once it settles: the page can grow while
        // scrolling (lazy images loading, scroll-in animations), leaving
        // the section short of where the scroll was aimed - so correct
        // and re-check (a few passes at most) before moving focus.
        (function scrollPass() {
            passes++;

            var maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            var top = Math.min(maxScroll, Math.max(0, window.scrollY + distanceFromRest(section)));

            window.scrollTo({
                top: top,
                // 'instant' rather than 'auto' - 'auto' would still follow
                // the stylesheet's scroll-behavior: smooth.
                behavior: smooth ? 'smooth' : 'instant'
            });

            afterScroll(function () {
                var off = Math.abs(distanceFromRest(section));
                var canMove = Math.abs(window.scrollY - Math.min(document.documentElement.scrollHeight - window.innerHeight, Math.max(0, window.scrollY + distanceFromRest(section)))) > 2;

                if (off > 4 && canMove && passes < 4) {
                    scrollPass();
                    return;
                }

                focusWithoutScrolling(focusTargetIn(section));
            });
        })();
    }

    document.addEventListener('click', function (event) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }

        var link = event.target.closest('a[href*="#"]');

        if (!link || (link.target && link.target !== '_self')) {
            return;
        }

        // Same page only - a link to an anchor on another page is a normal
        // navigation, handled on arrival (below).
        if (link.origin !== window.location.origin || link.pathname !== window.location.pathname || link.search !== window.location.search) {
            return;
        }

        var target = targetFromHash(link.hash);

        if (!target) {
            return;
        }

        event.preventDefault();

        if (window.location.hash === link.hash) {
            history.replaceState(null, '', link.hash);
        } else {
            history.pushState(null, '', link.hash);
        }

        goTo(target, { smooth: true });
    });

    // Back/forward between anchors.
    window.addEventListener('popstate', function () {
        var target = targetFromHash(window.location.hash);

        if (target) {
            goTo(target, { smooth: true });
        }
    });

    // Arriving with an #anchor (shared link, link from another page): the
    // browser has already jumped there (offset by scroll-margin-top); once
    // everything's loaded and layout has settled, line it up exactly and
    // move focus.
    window.addEventListener('load', function () {
        updateOffsetProperty();

        var target = targetFromHash(window.location.hash);

        if (target) {
            goTo(target, { smooth: false });
        }
    });

    window.addEventListener('resize', updateOffsetProperty);
    document.addEventListener('DOMContentLoaded', updateOffsetProperty);
})();
